import { useQuery, QueryClient } from "@tanstack/react-query";
import { Announcement, AnnouncementApiResponse, RawAnnouncement } from "src/types";
import { announcements } from "../components/templates/design1/components";

export const rawResponseDemo: AnnouncementApiResponse = {
    status: "success",
    total_announcement: announcements.length,
    total_announcement_month: 20,
    total_announcement_view: 20,
    total_announcement_month_view: 56,
    data: announcements as unknown as RawAnnouncement[],
    pagination: {
        current_page: 1,
        per_page: 10,
        total: 14,
        total_pages: 1
    }
}


const API_BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;
const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true"


export function transformRawAnnouncement(raw: RawAnnouncement): Announcement {
    return {
        title: raw.title,
        id: raw.id,
        description: raw.description,
        createdAt: raw.created_at,
        videoLink: raw.video_link as string,
        comments: 0,
        viewed: raw.views_count,
        image: raw.images[0]?.url || "/images/not-found-2.webp",
    };
}

export const fetchAnnouncements = async (
    page = 1,
    location: string
): Promise<[AnnouncementApiResponse, Announcement[]]> => {
    if (isDemo) {
        return [{} as AnnouncementApiResponse, announcements]
    }
    const res = await fetch(`${API_BASE_URL}api/v1/public/announcements/${location}?page=${page}`);
    if (!res.ok) throw new Error("Failed to fetch announcements");
    const rawData: AnnouncementApiResponse = await res.json();
    const transformed = rawData.data.map(transformRawAnnouncement);
    return [rawData, transformed];
};



// Add generics to clarify data and error types
export const useAnnouncements = (location: string, page: number) => {
    return useQuery<[AnnouncementApiResponse, Announcement[]], Error>({
        queryKey: ["announcements", page],
        queryFn: () => fetchAnnouncements(page, location),
        staleTime: 0,
        retry: false,
        enabled: !!location,

    });
};

export const prefetchAnnouncements = async (queryClient: QueryClient, location: string) => {
    await queryClient.prefetchQuery({
        queryKey: ["announcements", 1],
        queryFn: () => fetchAnnouncements(1, location),
        staleTime: 0,
    });
};



import { useInfiniteQuery, QueryFunctionContext } from "@tanstack/react-query";


type QueryKey = [string, string | undefined, string | undefined];
type Data = [AnnouncementApiResponse, Announcement[]];

export const useAnnouncementsInfinite = ({
    location,
    sort,
    search,
}: {
    location: string;
    sort?: string;
    search?: string;
}) => {
    return useInfiniteQuery<
        Data,      // TData: the data returned by useInfiniteQuery (pages)
        Error,     // TError
        Data,      // TQueryFnData: data returned by the query function
        QueryKey,  // TQueryKey
        number    // TPageParam
    >({
        initialPageParam: 1,
        queryKey: ["announcementsInfinite", sort, search],
        queryFn: async (context: QueryFunctionContext<QueryKey, number>) => {
            const [, ctxSort, ctxSearch] = context.queryKey;
            const pageParam = context.pageParam as number || 1;
            return fetchAnnouncementsInfinite({
                location: location,
                page: pageParam,
                sort: ctxSort,
                search: ctxSearch,
            });
        },
        getNextPageParam: (lastPage) => {
            const rawResponse = lastPage[0];
            const currentPage = rawResponse.pagination.current_page;
            const totalPages = rawResponse.pagination?.total_pages ?? 1;
            return currentPage < totalPages ? currentPage + 1 : undefined;
        },
        staleTime: 0,
        retry: false,
        enabled: !!location,
    });
};





export const fetchAnnouncementsInfinite = async ({
    location,
    page = 1,
    sort,
    search,
}: {
    location: string;
    page?: number;
    sort?: string;
    search?: string;
}): Promise<[AnnouncementApiResponse, Announcement[]]> => {
    const params = new URLSearchParams();
    const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
    if (isDemo) {
        return [rawResponseDemo, announcements]
    }
    params.append("page", String(page));
    if (sort) params.append("sort_by", sort);
    if (search) params.append("search", search);
    const res = await fetch(
        `${API_BASE_URL}api/v1/public/announcements/${location}?${params.toString()}`
    );
    if (!res.ok) throw new Error("Failed to fetch announcements");
    const rawData: AnnouncementApiResponse = await res.json();
    const transformed = rawData.data.map(transformRawAnnouncement);
    return [rawData, transformed];
};

export async function prefetchAnnouncementsInfinite(
    queryClient: QueryClient,
    {
        location,
        sort,
        search,
    }: {
        location: string;
        sort?: string;
        search?: string;
    }
) {
    await queryClient.prefetchInfiniteQuery({
        queryKey: ["announcementsInfinite", sort, search],
        queryFn: ({ pageParam = 1 }) =>
            fetchAnnouncementsInfinite({ location, page: pageParam, sort, search }),
        initialPageParam: 1, // ✅ required by React Query v5+
        staleTime: 0,

    });
}


